import { Box, CircularProgress, Skeleton, Stack } from "@mui/material";
import { ChatCategoryFilter } from "Components/Chat/ChatCategoryFilter";
import { ChatSearchField } from "Components/Chat/ChatSearchField";
import { Resources, useResource } from "Translations/Resources";

const noop = () => {};

type Props = {
  isMobile: boolean;
  isChatDetailView: boolean;
};

export const ChannelListItemsSkeleton = () => (
  <Box px={1}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Box key={i} display="flex" alignItems="center" gap={1.5} py={1.5}>
        <Skeleton variant="circular" width={48} height={48} />
        <Box flex={1}>
          <Skeleton variant="text" width="60%" sx={{ fontSize: 16 }} />
          <Skeleton variant="text" width="40%" sx={{ fontSize: 14 }} />
        </Box>
      </Box>
    ))}
  </Box>
);

const ChannelListSkeleton = ({ isMobile }: { isMobile: boolean }) => {
  const { t } = useResource();

  return (
    <Stack
      width={isMobile ? "100%" : 400}
      minWidth={isMobile ? undefined : 400}
      height="100%"
      borderRight={isMobile ? "none" : "1px solid"}
      borderColor="divider"
      position="relative"
      gap={3}
    >
      <Stack px={2} gap={3}>
        <ChatCategoryFilter
          value="All"
          onChange={noop}
          pendingRequestCount={0}
        />
        <ChatSearchField
          value=""
          onChange={noop}
          placeholder={t(Resources.Chat.Layout.SearchPlaceholder)}
        />
      </Stack>
      <Box flex={1} minHeight={0}>
        <ChannelListItemsSkeleton />
      </Box>
      <Skeleton
        variant="circular"
        width={56}
        height={56}
        sx={{ position: "absolute", bottom: 24, right: 24 }}
      />
    </Stack>
  );
};

const MessagePanelSkeleton = () => (
  <Stack flex={1}>
    <Box
      display="flex"
      alignItems="center"
      bgcolor="primary.main"
      p={2.5}
      gap={1}
      flexShrink={0}
    >
      <Skeleton
        variant="circular"
        width={40}
        height={40}
        sx={{ bgcolor: "rgba(255,255,255,0.2)" }}
      />
      <Skeleton
        variant="text"
        width={120}
        sx={{ bgcolor: "rgba(255,255,255,0.2)", fontSize: 16 }}
      />
    </Box>
    <Stack alignItems="center" justifyContent="center" flex={1}>
      <CircularProgress />
    </Stack>
  </Stack>
);

export const ChatLoadingSkeleton = ({ isMobile, isChatDetailView }: Props) => {
  if (!isMobile) {
    return (
      <Box display="flex" flex={1} height="100%">
        <ChannelListSkeleton isMobile={false} />
        <MessagePanelSkeleton />
      </Box>
    );
  }

  if (isChatDetailView) {
    return <MessagePanelSkeleton />;
  }

  return <ChannelListSkeleton isMobile />;
};
